(function (root) {
  const EXPERIMENT_ID = 'also-like-orders-ab';

  function selectOriginalProducts(products) {
    const seen = new Set();
    return products.filter(item => {
      if (!item.renderable || !item.productId || seen.has(item.productId)) return false;
      seen.add(item.productId);
      return true;
    }).slice(0, 8);
  }

  function sameColorFamily(left, right) {
    return Boolean(left.businessId && right.businessId) && (left.businessId === right.businessId ||
      left.colorIds.includes(right.businessId) || right.colorIds.includes(left.businessId));
  }

  function selectMixedProducts(pools) {
    const used = new Set();
    const fixed = [5, 6, 7, 8].map(rank => {
      const item = pools.shopify.find(candidate => candidate.sourceRank === rank);
      if (!item?.eligible || !item.productId || used.has(item.productId)) return null;
      used.add(item.productId);
      return item;
    });
    const supplements = [];
    // Use available candidates even when the response contains only part of the ranking.
    for (const candidate of pools.orders) {
      if (candidate.sourceRank > 50 || !candidate.eligible || !candidate.productId || used.has(candidate.productId)) continue;
      // Missing color metadata yields an empty list; still check either side's known relations.
      if (fixed.some(item => item && sameColorFamily(candidate, item))) continue;
      supplements.push(candidate);
      used.add(candidate.productId);
      if (supplements.length === 4) break;
    }
    for (const candidate of pools.bestSellers) {
      if (supplements.length === 4) break;
      if (candidate.sourceRank > 30 || !candidate.eligible || !candidate.productId || used.has(candidate.productId)) continue;
      supplements.push(candidate);
      used.add(candidate.productId);
    }
    return {
      items: [supplements[0], supplements[1], fixed[0], fixed[1], supplements[2], supplements[3], fixed[2], fixed[3]].filter(Boolean),
      reason: '',
      strategy: 'mixed',
    };
  }

  async function resolveAssignment(country, signal, environment = root) {
    if (environment.Shopify?.designMode) return { group: '', status: 'editor_preview' };
    if (country === 'TH') return { group: '', status: 'excluded_th' };
    const started = Date.now();
    while (!signal.aborted) {
      try {
        const value = environment._growthbook?.getFeatureValue?.(EXPERIMENT_ID, null);
        if (value !== undefined && value !== null) {
          if (value === 'a' || value === 'b') return { group: value, status: 'assigned' };
          return { group: '', status: 'error' };
        }
      } catch (_error) {
        return { group: '', status: 'error' };
      }
      if (Date.now() - started >= 3000) return { group: '', status: 'timeout' };
      await new Promise(resolve => {
        const finish = () => {
          environment.clearTimeout(timer);
          signal.removeEventListener('abort', finish);
          resolve();
        };
        const timer = environment.setTimeout(finish, 100);
        signal.addEventListener('abort', finish, { once: true });
      });
    }
    return { group: '', status: 'aborted' };
  }

  function parseCandidatePools(html, context) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const response = parsed.querySelector('[data-also-like-response]');
    if (!response || response.dataset.productId !== context.productId || response.dataset.country !== context.country) {
      throw new Error('invalid_response_context');
    }
    const readPool = (pool, source) => Array.from(response.querySelectorAll(
      `[data-candidate-pool="${pool}"] > template[data-also-like-candidate]`
    )).map(template => {
      const data = template.dataset;
      let colorIds = [];
      let colorKnown = data.colorKnown === 'true';
      try {
        const values = JSON.parse(data.colorIds);
        if (Array.isArray(values)) colorIds = values.map(String);
        else colorKnown = false;
      } catch (_error) { colorKnown = false; }
      const hasCard = Boolean(template.content.querySelector('.fabrique-product-wrapper'));
      return {
        productId: data.productId,
        businessId: data.businessId,
        sourceRank: Number(data.sourceRank),
        source,
        renderable: hasCard && data.renderable === 'true',
        eligible: hasCard && data.eligible === 'true',
        colorKnown,
        colorIds,
        template,
      };
    });
    let orderIds = null;
    try { orderIds = JSON.parse(response.dataset.orderIds); } catch (_error) { /* Use available candidate templates. */ }
    return {
      shopify: readPool('shopify', 'shopify'),
      orders: readPool('all_orders', 'all_orders'),
      bestSellers: readPool('best_seller', 'best_seller'),
      orderIds,
    };
  }

  function decorateCard(card, item, attribution) {
    Object.assign(card.dataset, {
      recommendationContext: 'true',
      trackModule: 'you_may_also_like',
      trackModuleType: 'list_horizontal',
      abExperimentId: attribution.group ? EXPERIMENT_ID : '',
      abGroup: attribution.group,
      trackAbtestExperimentId: attribution.group ? EXPERIMENT_ID : '',
      trackAbtestExperimentGroup: attribution.group,
      trackAddToCart: 'true',
      // Scope deduplication to this rendered module, rather than another occurrence of this product.
      trackKey: `${attribution.requestId}:${item.productId}`,
    });
  }

  if (!root.customElements || root.customElements.get('also-like-recommendations')) return;

  class AlsoLikeRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.requestId = root.crypto?.randomUUID?.() || `also-like-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      root.clearTimeout(this.requestTimer);
      this.trackTimers?.forEach(timer => root.clearTimeout(timer));
      this.exposureObserver?.disconnect();
    }

    async load() {
      const context = { country: this.dataset.country, productId: this.dataset.productId };
      const controller = this.controller;
      try {
        if (!context.productId) throw new Error('missing_source_product');
        const assignmentPromise = resolveAssignment(context.country, controller.signal);
        const responsePromise = this.fetchPools(controller.signal).then(html => parseCandidatePools(html, context));
        const [assignment, pools] = await Promise.all([assignmentPromise, responsePromise]);
        if (!this.isConnected || controller.signal.aborted ||
            this.dataset.productId !== context.productId || this.dataset.country !== context.country) return;
        const outcome = assignment.group === 'b'
          ? selectMixedProducts(pools)
          : { items: selectOriginalProducts(pools.shopify), reason: '', strategy: 'shopify' };
        this.attribution = { ...assignment, ...outcome, requestId: this.requestId };
        this.mount(outcome.items);
        if (!root.Shopify?.designMode) this.observeExposure();
      } catch (error) {
        if (!this.isConnected) return;
        controller.abort();
        this.attribution = { group: '', status: 'error', strategy: 'shopify', reason: error.name === 'AbortError' ? 'recommendations_timeout' : error.message, items: [] };
        this.closest('.product-feed').hidden = true;
      } finally {
        this.setAttribute('aria-busy', 'false');
      }
    }

    async fetchPools(signal) {
      this.requestTimer = root.setTimeout(() => this.controller.abort(), 5000);
      try {
        const response = await fetch(this.dataset.url, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('recommendations_request_failed');
        return await response.text();
      } finally {
        root.clearTimeout(this.requestTimer);
      }
    }

    mount(items) {
      const feed = this.closest('.product-feed');
      if (!items.length) { feed.hidden = true; return; }
      const shell = document.createElement('template');
      shell.innerHTML = '<div class="also-like-grid"></div>';
      const grid = shell.content.firstElementChild;
      items.forEach(item => {
        const fragment = item.template.content.cloneNode(true);
        const cell = fragment.firstElementChild;
        cell.classList.remove('swiper-slide', 'card-product-slider__slide');
        cell.classList.add('also-like-grid__item');
        decorateCard(fragment.querySelector('.fabrique-product-wrapper'), item, this.attribution);
        grid.append(fragment);
      });
      // Adopt inert template contents before upgrading and initializing product cards.
      document.adoptNode(grid);
      root.customElements.upgrade(grid);
      this.replaceChildren(grid);
      this.classList.remove('swiper', 'card-product-slider');
      feed.classList.remove('hidden');
      feed.hidden = false;
    }

    modulePayload() {
      const info = this.attribution;
      return {
        moduleId: 'you_may_also_like',
        moduleType: 'list_horizontal',
        trackItems: [{
          abtestExperimentId: info.group ? EXPERIMENT_ID : '',
          abtestExperimentGroup: info.group,
        }],
      };
    }

    reportWhenReady(event, payload) {
      this.trackTimers ||= new Set();
      const deadline = Date.now() + 5000;
      const attempt = () => {
        if (!this.isConnected) return;
        if (root.$track?.track) { root.$track.track(event, payload); return; }
        if (Date.now() < deadline) {
          const timer = root.setTimeout(() => {
            this.trackTimers.delete(timer);
            attempt();
          }, 100);
          this.trackTimers.add(timer);
        }
      };
      attempt();
    }

    observeExposure() {
      if (!this.attribution.items.length) return;
      const feed = this.closest('.product-feed');
      this.exposureObserver = new IntersectionObserver(entries => {
        if (this.exposureReported || !entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        this.exposureReported = true;
        this.exposureObserver.disconnect();
        this.reportWhenReady('ModuleExpose', this.modulePayload());
      }, { threshold: 0.5 });
      this.exposureObserver.observe(feed);
    }
  }

  root.customElements.define('also-like-recommendations', AlsoLikeRecommendations);
})(typeof window === 'undefined' ? globalThis : window);
